import { useEffect, useState, useCallback } from 'react'
import {
  Box, Typography, Paper, Grid, Chip, Button, Stack, CircularProgress, Alert, Divider,
} from '@mui/material'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import LockIcon from '@mui/icons-material/Lock'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import { useAuth } from '../../auth/AuthContext'
import { useNotify } from '../../components/Notifications'
import * as catalogos from '../../api/catalogos'
import * as cobrosApi from '../../api/cobros'
import ProcesoCobroModal from '../Facturar/ProcesoCobroModal'
import ArqueoModal from './ArqueoModal'

export default function CajaPage() {
  const { auth } = useAuth()
  const notify = useNotify()
  const caja = auth?.caja || null
  const usuario = auth?.accesoSistema?.usuario || null
  const acceso = auth?.accesos?.find((a) => a.accesoPorDefecto) || auth?.accesos?.[0] || null

  const [loading, setLoading] = useState(true)
  const [proceso, setProceso] = useState(null)
  const [estadoAbierto, setEstadoAbierto] = useState(null)
  const [aperturaOpen, setAperturaOpen] = useState(false)
  const [arqueoOpen, setArqueoOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      if (!estadoAbierto) {
        const estados = await catalogos.getEstadosProcesoCobro()
        setEstadoAbierto((Array.isArray(estados) ? estados : []).find((t) => t?.codigo === 'estado_procesocobro_abierto') || null)
      }
      if (caja?.id && usuario?.id) {
        const pc = await cobrosApi.buscarProcesoCobroAbierto({ cajaId: caja.id, usuarioId: usuario.id })
        setProceso(pc)
      }
    } catch (e) {
      notify.error('No se pudo cargar el estado de la caja.')
    } finally { setLoading(false) }
  }, [caja?.id, usuario?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { cargar() }, [cargar])

  const abrir = async ({ montoBaseEfectivo }) => {
    setBusy(true)
    try {
      await cobrosApi.abrirProcesoCobro({ estadoAbiertoId: estadoAbierto?.id, caja, usuario, locacionId: auth?.locacionId, montoBaseEfectivo })
      notify.success('Caja abierta.')
      setAperturaOpen(false)
      cargar()
    } catch (e) { notify.error(e?.response?.data?.[0]?.message || 'No se pudo abrir la caja.') }
    finally { setBusy(false) }
  }

  const cerrar = async () => {
    if (!proceso?.id) return
    if (!window.confirm('¿Cerrar la caja? Se totalizan los cobros del día y se cierra el proceso de cobro.')) return
    setBusy(true)
    try {
      await cobrosApi.cerrarProcesoCobro(proceso.id, acceso?.id)
      notify.success('Caja cerrada.')
      cargar()
    } catch (e) { notify.error(e?.response?.data?.[0]?.message || 'No se pudo cerrar la caja.') }
    finally { setBusy(false) }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>

  const abierta = !!proceso

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>Caja</Typography>

      {!caja && <Alert severity="warning" sx={{ mb: 2 }}>El usuario no tiene una caja registrada para su sucursal.</Alert>}

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
          <Chip label={`Caja: ${caja?.nombre || '—'}`} />
          <Chip label={`Usuario: ${usuario?.username || auth?.username || '—'}`} />
          <Chip color={abierta ? 'success' : 'default'} label={abierta ? 'ABIERTA' : 'CERRADA'} />
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {abierta ? (
          <Grid container spacing={2}>
            <Info label="Proceso de cobro" value={`#${proceso.id}`} />
            <Info label="Apertura" value={proceso.fechaHoraInicio || '—'} />
            <Info label="Monto base efectivo" value={proceso.montoBaseEfectivo ?? '—'} />
            <Info label="Estado" value={proceso.estado?.descripcion || proceso.estado?.codigo || 'Abierto'} />
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} mt={1}>
                <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => setArqueoOpen(true)}>Arqueo</Button>
                <Button variant="contained" color="secondary" startIcon={<LockIcon />} onClick={cerrar} disabled={busy}>
                  {busy ? <CircularProgress size={22} color="inherit" /> : 'Cerrar caja'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Stack spacing={2} alignItems="flex-start">
            <Typography color="text.secondary">No hay un proceso de cobro abierto para hoy. Abrí la caja para empezar a operar.</Typography>
            <Button variant="contained" color="secondary" startIcon={<LockOpenIcon />} onClick={() => setAperturaOpen(true)} disabled={!caja || !estadoAbierto}>
              Abrir caja
            </Button>
          </Stack>
        )}
      </Paper>

      <ProcesoCobroModal open={aperturaOpen} caja={caja} usuario={usuario?.username} loading={busy} onConfirm={abrir} onClose={() => setAperturaOpen(false)} />
      <ArqueoModal open={arqueoOpen} proceso={proceso} caja={caja} usrCre={usuario?.username || auth?.username} onClose={() => setArqueoOpen(false)} onDone={cargar} />
    </Box>
  )
}

function Info({ label, value }) {
  return (
    <Grid item xs={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1">{value}</Typography>
    </Grid>
  )
}
